// src/components/admin/flows/canvas/useConnectNodes.js
import { useState, useRef, useCallback } from 'react';
import { NODE_WIDTH, NODE_HEIGHT } from './nodeRegistry';

const useConnectNodes = ({ zoom, pan, steps, onConnect }) => {
    const [connectingFrom, setConnectingFrom] = useState(null);
    const [mousePos, setMousePos] = useState(null);
    const canvasRef = useRef(null);

    const startConnection = useCallback((e, stepId, branch = null) => {
        e.stopPropagation();
        e.preventDefault();
        setConnectingFrom({ stepId, branch });
    }, []);

    const updateConnection = useCallback((e) => {
        if (!connectingFrom || !canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        setMousePos({
            x: (e.clientX - rect.left - pan.x) / zoom,
            y: (e.clientY - rect.top - pan.y) / zoom,
        });
    }, [connectingFrom, zoom, pan]);

    const endConnection = useCallback((e, targetStepId) => {
        if (!connectingFrom) return;

        if (targetStepId && targetStepId !== connectingFrom.stepId) {
            onConnect({
                from_step_id: connectingFrom.stepId,
                to_step_id: targetStepId,
                label: connectingFrom.branch === 'true' ? 'Yes' : connectingFrom.branch === 'false' ? 'No' : null,
                condition_branch: connectingFrom.branch,
            });
        }

        setConnectingFrom(null);
        setMousePos(null);
    }, [connectingFrom, onConnect]);

    const cancelConnection = useCallback(() => {
        setConnectingFrom(null);
        setMousePos(null);
    }, []);

    // Get the starting position for the temporary line
    const getConnectionStart = useCallback(() => {
        if (!connectingFrom) return null;
        const step = steps.find(s => s.id === connectingFrom.stepId);
        if (!step) return null;

        // For condition nodes, offset left/right for branches
        if (step.type === 'condition' && connectingFrom.branch) {
            const xOffset = connectingFrom.branch === 'true' ? NODE_WIDTH * 0.3 : NODE_WIDTH * 0.7;
            return {
                x: step.position.x + xOffset,
                y: step.position.y + NODE_HEIGHT,
            };
        }

        return {
            x: step.position.x + NODE_WIDTH / 2,
            y: step.position.y + NODE_HEIGHT,
        };
    }, [connectingFrom, steps]);

    return {
        connectingFrom,
        mousePos,
        canvasRef,
        startConnection,
        updateConnection,
        endConnection,
        cancelConnection,
        getConnectionStart,
    };
};

export default useConnectNodes;
